/**
 * REMUDA storefront: product configurator, image stacks and the slide-out bag.
 * Prices, variant IDs and stock come from window.RemudaCatalog (snippets/remuda-data.liquid).
 * The bag is the real Shopify cart: /cart.js, /cart/add.js and /cart/change.js.
 */

(function () {
  const CATALOG = window.RemudaCatalog || {};
  const CABIN = 'remuda-cabin-55';
  const CHECKIN_L = 'remuda-checkin-77-belted';
  const SET_SAVING = 12000;

  const SIZE_SPECS = {
    'remuda-cabin-s': {
      code: 'ORIGINAL CABIN S',
      name: 'Cabin S',
      dims: '55 × 40 × 20 cm',
      volume: '31 L',
      weight: '3.6 kg',
      duration: 'Slim carry-on for 1 to 3 days',
      alloy: 'Anodized Aluminum-Magnesium Alloy · Riveted Aluminum Corners',
      fits: '1 – 3 Days of Apparel · Toiletry Kit · 14" Laptop'
    },
    'remuda-cabin-55': {
      code: 'ORIGINAL CABIN',
      name: 'Cabin',
      dims: '55 × 40 × 23 cm',
      volume: '36 L',
      weight: '4.1 kg',
      duration: 'Carry-on for 2 to 4 days',
      alloy: 'Anodized Aluminum-Magnesium Alloy · Riveted Aluminum Corners',
      fits: '2 – 4 Days of Apparel · 1 Pair Footwear · Toiletry Kit · 16" Laptop'
    },
    'remuda-checkin-67': {
      code: 'ORIGINAL CHECK-IN M',
      name: 'Check-In M',
      dims: '67 × 45 × 26 cm',
      volume: '65 L',
      weight: '5.3 kg',
      duration: 'Engineered for 5 to 8 days of travel',
      alloy: 'Anodized Aluminum-Magnesium Alloy · Riveted Aluminum Corners',
      fits: '5 – 8 Days of Apparel · 2 – 3 Pairs Footwear · Packing Cube Set'
    },
    'remuda-checkin-77-belted': {
      code: 'ORIGINAL CHECK-IN L',
      name: 'Check-In L',
      dims: '77 × 51 × 28 cm',
      volume: '88 L',
      weight: '6.1 kg',
      duration: 'Engineered for 10 to 14 days of long-haul travel',
      alloy: 'Anodized Aluminum-Magnesium Alloy · Dual Flush TSA Locks',
      fits: '10 – 14 Days Full Wardrobe · Outerwear · 3 – 4 Pairs Footwear'
    },
    'remuda-overland-trunk-80': {
      code: 'ORIGINAL TRUNK PLUS',
      name: 'Trunk Plus',
      dims: '80 × 43.5 × 46 cm',
      volume: '105 L',
      weight: '6.8 kg',
      duration: 'Deep-cavity trunk proportion for 14+ days of travel',
      alloy: 'Anodized Aluminum-Magnesium Alloy · Riveted Aluminum Corners',
      fits: '14+ Days Wardrobe · Deep-Cavity Gear, Winter Outerwear & Equipment'
    }
  };

  const state = {
    handle: CABIN,
    colourKey: 'silver',
    colourLabel: 'Silver',
    updateUrl: false,
    cart: null,
    cartError: ''
  };

  // Shoppers abroad see Shopify's converted prices, so symbol and separators come from
  // a sample price (12,345.67) rendered by Liquid, the same way as money_without_trailing_zeros.
  const MONEY_PARTS = /^(.*?)12(\D?)345(\D)67(.*)$/.exec(window.RemudaMoneySample || '') || [null, '$', ',', '.', ''];
  const money = (cents) => {
    const [, prefix, thousands, decimal, suffix] = MONEY_PARTS;
    const whole = String(Math.floor(cents / 100)).replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    const fraction = cents % 100 ? decimal + String(cents % 100).padStart(2, '0') : '';
    return prefix + whole + fraction + suffix;
  };

  const SALE = window.RemudaSale || { on: false };
  const salePrice = (handle, cents) => (SALE.on && handle !== SALE.excluded ? cents - SALE.amount : cents);

  const escapeHtml = (value) => String(value == null ? '' : value).replace(/[&<>"']/g, (c) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;'
  }[c]));

  const setTxt = (id, val) => {
    const el = document.getElementById(id);
    if (el) el.textContent = val;
  };

  const variantFor = (handle, colourKey) => {
    const size = CATALOG[handle];
    return size && size.variants ? size.variants[colourKey] : null;
  };

  const sizedImage = (url, width) => {
    if (!url) return '';
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  };

  const postJson = async (url, body) => {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.description || data.message || 'Something went wrong. Please try again.');
    return data;
  };

  window.RemudaStore = {
    init() {
      const section = document.getElementById('bespoke-configurator');
      if (section) {
        state.handle = section.dataset.handle || CABIN;
        state.colourKey = section.dataset.colour || 'silver';
        state.colourLabel = section.dataset.colourLabel || 'Silver';
        state.updateUrl = section.dataset.productPage === 'true';
      }
      this.bindCardHoverStacks();
      this.bindStickyPdpBar();
      this.bindFeatureSlider();
      this.refreshCart();
      window.addEventListener('pageshow', (e) => {
        if (e.persisted) this.refreshCart();
      });
    },

    bindStickyPdpBar() {
      const stickyBar = document.getElementById('remudaStickyPdpBar');
      const mainBtn = document.getElementById('mainPdpPurchaseBtn');
      if (stickyBar && mainBtn) {
        // Only once the main Add to Cart button has scrolled off the top of the screen.
        const updateStickyVisibility = () => {
          const rect = mainBtn.getBoundingClientRect();
          stickyBar.classList.toggle('visible', rect.bottom < 0);
        };
        window.addEventListener('scroll', updateStickyVisibility, { passive: true });
        window.addEventListener('resize', updateStickyVisibility, { passive: true });
        updateStickyVisibility();
      }

      document.addEventListener('click', (e) => {
        const dd = document.getElementById('remudaSizeDropdown');
        if (dd && dd.classList.contains('open') && !dd.contains(e.target)) {
          dd.classList.remove('open');
        }
      });
    },

    bindFeatureSlider() {
      const slider = document.getElementById('remudaFeatureSlider');
      if (!slider) return;
      slider.addEventListener('scroll', () => {
        if (window.innerWidth > 768) return;
        const cards = slider.querySelectorAll('.remuda-feature-card');
        if (!cards.length) return;
        const cardWidth = cards[0].offsetWidth || 1;
        const idx = Math.max(0, Math.min(cards.length - 1, Math.round(slider.scrollLeft / cardWidth)));
        document.querySelectorAll('.feature-dot').forEach((d, i) => d.classList.toggle('active', i === idx));
      }, { passive: true });
    },

    scrollFeatureSlide(idx, btnEl) {
      document.querySelectorAll('.feature-dot').forEach((d, i) => d.classList.toggle('active', i === idx));
      const slider = document.getElementById('remudaFeatureSlider');
      const cards = slider?.querySelectorAll('.remuda-feature-card');
      if (cards && cards[idx]) {
        cards[idx].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    },

    bindCardHoverStacks() {
      document.querySelectorAll('[data-card-stack]').forEach(card => {
        const media = card.querySelector('.luggage-media');
        if (!media) return;

        media.addEventListener('mousemove', (e) => {
          if (window.innerWidth <= 768) return;
          const rect = media.getBoundingClientRect();
          const relX = Math.max(0, Math.min(0.999, (e.clientX - rect.left) / rect.width));
          const slides = card.querySelectorAll('.stack-slide');
          if (!slides.length) return;
          const idx = Math.floor(relX * slides.length);
          this.activateCardSlide(card, idx);
        });

        media.addEventListener('mouseleave', () => {
          if (window.innerWidth <= 768) return;
          this.activateCardSlide(card, 0);
        });
      });
    },

    setCardSlide(dotBtn, slideIdx) {
      const card = dotBtn.closest('[data-card-stack]');
      if (!card) return;
      this.activateCardSlide(card, slideIdx);
    },

    activateCardSlide(card, slideIdx) {
      const slides = card.querySelectorAll('.stack-slide');
      const dots = card.querySelectorAll('.stack-dot');
      slides.forEach((s, i) => s.classList.toggle('active', i === slideIdx));
      dots.forEach((d, i) => d.classList.toggle('active', i === slideIdx));
    },

    openDrawer() {
      document.getElementById('remudaCartOverlay')?.classList.add('open');
      document.getElementById('remudaCartDrawer')?.classList.add('open');
      this.refreshCart();
    },

    closeDrawer() {
      document.getElementById('remudaCartOverlay')?.classList.remove('open');
      document.getElementById('remudaCartDrawer')?.classList.remove('open');
    },

    openMobileMenu() {
      document.getElementById('remudaMobileMenuOverlay')?.classList.add('open');
      document.getElementById('remudaMobileMenuDrawer')?.classList.add('open');
    },

    closeMobileMenu() {
      document.getElementById('remudaMobileMenuOverlay')?.classList.remove('open');
      document.getElementById('remudaMobileMenuDrawer')?.classList.remove('open');
    },

    toggleSizeDropdown() {
      const dd = document.getElementById('remudaSizeDropdown');
      const btn = document.getElementById('remudaSizeDropdownBtn');
      if (!dd) return;
      const isOpen = dd.classList.toggle('open');
      if (btn) btn.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    },

    selectRemudaSize(btnEl) {
      document.querySelectorAll('.remuda-dropdown-option').forEach(b => b.classList.remove('active'));
      btnEl.classList.add('active');
      document.getElementById('remudaSizeDropdown')?.classList.remove('open');

      const { handle, name, dims, weight, vol } = btnEl.dataset;
      state.handle = handle;

      setTxt('pdpProductTitle', name);
      setTxt('pdpDropdownSizeName', name);
      setTxt('pdpInlineTitle', name);
      setTxt('stickyPdpTitle', name);
      setTxt('pdpSizeDimLabel', dims + ' · ' + vol);
      setTxt('accDims', dims);
      setTxt('accWeight', weight);
      setTxt('accVol', vol);

      this.refreshConfigurator();
    },

    selectRemudaColour(colourKey, colourLabel, btnEl) {
      document.querySelectorAll('.remuda-colour-btn').forEach(b => b.classList.remove('active'));
      if (btnEl) btnEl.classList.add('active');

      state.colourKey = colourKey;
      state.colourLabel = colourLabel;

      setTxt('pdpColorLabel', colourLabel);
      setTxt('stickyPdpColor', colourLabel);
      setTxt('bundleColour', colourLabel);

      this.refreshConfigurator();
    },

    // Price, stock, buttons, photos and URL for the chosen size and colour.
    refreshConfigurator() {
      const variant = variantFor(state.handle, state.colourKey);
      if (!variant) return;
      const now = salePrice(state.handle, variant.price);
      const price = money(now);

      const live = document.getElementById('pdpLivePrice');
      if (live) live.innerHTML = (now !== variant.price ? '<s>' + money(variant.price) + '</s> ' : '') + price;
      const saleTag = document.getElementById('pdpSaleTag');
      if (saleTag) saleTag.hidden = now === variant.price;
      setTxt('pdpDropdownPrice', price);
      setTxt('stickyPdpPrice', price);
      setTxt('pdpStockText', variant.available ? 'Available' : 'Sold out');
      document.getElementById('pdpStockBadge')?.classList.toggle('sold-out', !variant.available);

      ['mainPdpPurchaseBtn', 'stickyPdpBtn'].forEach((id) => {
        const btn = document.getElementById(id);
        if (!btn) return;
        btn.disabled = !variant.available;
        btn.textContent = variant.available ? 'Add to Cart' : 'Sold Out';
      });

      document.querySelectorAll('.remuda-dropdown-option').forEach((option) => {
        const optionVariant = variantFor(option.dataset.handle, state.colourKey);
        const priceEl = option.querySelector('.dropdown-opt-price');
        if (optionVariant && priceEl) priceEl.textContent = money(salePrice(option.dataset.handle, optionVariant.price));
      });

      const cabin = variantFor(CABIN, state.colourKey);
      const checkin = variantFor(CHECKIN_L, state.colourKey);
      if (cabin && checkin) {
        const setTotal = cabin.price + checkin.price;
        setTxt('bundleOldPrice', money(setTotal));
        setTxt('bundleNewPrice', money(setTotal - SET_SAVING));
        setTxt('bundleBtnPrice', money(setTotal - SET_SAVING));
      }

      this.refreshStack();

      if (state.updateUrl && CATALOG[state.handle]?.url) {
        history.replaceState(null, '', CATALOG[state.handle].url + '?variant=' + variant.id);
      }
    },

    refreshStack() {
      const sectionEl = document.getElementById('bespoke-configurator');
      if (!sectionEl) return;
      const option = document.querySelector('.remuda-dropdown-option.active');
      const hero = option ? option.getAttribute('data-hero-' + state.colourKey) : '';

      const setShot = (i, src) => {
        if (!src) return;
        const imgEl = document.getElementById('stackImg' + i);
        const railEl = document.getElementById('railImg' + i);
        if (imgEl) imgEl.src = src;
        if (railEl) railEl.src = src;
      };

      setShot(1, hero);
      const stickyThumb = document.getElementById('stickyPdpThumb');
      if (stickyThumb && hero) stickyThumb.src = hero;
      for (let i = 2; i <= 5; i++) {
        setShot(i, sectionEl.getAttribute('data-' + state.colourKey + '-' + i));
      }
    },

    toggleAirlineModal(open) {
      const modal = document.getElementById('remudaAirlineModal');
      if (modal) modal.classList.toggle('open', !!open);
    },

    scrollToStackTile(tileIdx, btnEl) {
      document.querySelectorAll('.rail-thumb').forEach(b => b.classList.remove('active'));
      if (btnEl) btnEl.classList.add('active');
      const tileEl = document.getElementById(`pdpStackTile${tileIdx}`);
      if (tileEl) {
        tileEl.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    },

    selectSizeSpec(handle, btnEl) {
      const spec = SIZE_SPECS[handle];
      if (!spec) return;

      document.querySelectorAll('.size-tab-btn').forEach(b => b.classList.remove('active'));
      if (btnEl) btnEl.classList.add('active');

      setTxt('specCode', spec.code);
      setTxt('specName', spec.name);
      setTxt('specDims', spec.dims);
      setTxt('specVolume', spec.volume);
      setTxt('specWeight', spec.weight);
      setTxt('specDuration', spec.duration);
      setTxt('specAlloy', spec.alloy);
      setTxt('specFits', spec.fits);

      const prices = Object.values(CATALOG[handle]?.variants || {}).map(v => salePrice(handle, v.price));
      if (prices.length) setTxt('specPriceCompare', 'From ' + money(Math.min(...prices)) + ' USD');
    },

    addConfiguredFlagship(btnEl) {
      const variant = variantFor(state.handle, state.colourKey);
      if (variant) this.addVariant(variant.id, btnEl);
    },

    addMatchedSet(btnEl) {
      const cabin = variantFor(CABIN, state.colourKey);
      const checkin = variantFor(CHECKIN_L, state.colourKey);
      if (!cabin || !checkin) return;
      this.addToCart([{ id: cabin.id, quantity: 1 }, { id: checkin.id, quantity: 1 }], btnEl);
    },

    addVariant(variantId, btnEl) {
      return this.addToCart([{ id: variantId, quantity: 1 }], btnEl);
    },

    async addToCart(items, btnEl) {
      const label = btnEl ? btnEl.innerHTML : '';
      if (btnEl) {
        btnEl.disabled = true;
        btnEl.textContent = 'Adding...';
      }
      state.cartError = '';
      try {
        await postJson('/cart/add.js', { items });
      } catch (err) {
        state.cartError = err.message;
      }
      if (btnEl) {
        btnEl.disabled = false;
        btnEl.innerHTML = label;
      }
      this.openDrawer();
      if (window.location.pathname === '/cart' && !state.cartError) window.location.reload();
    },

    async changeLine(key, quantity) {
      state.cartError = '';
      try {
        state.cart = await postJson('/cart/change.js', { id: key, quantity });
      } catch (err) {
        state.cartError = err.message;
      }
      this.render();
      if (window.location.pathname === '/cart' && !state.cartError) window.location.reload();
    },

    async refreshCart() {
      try {
        const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        state.cart = await res.json();
      } catch (err) {
        console.warn('Could not read the cart:', err);
      }
      this.render();
    },

    render() {
      const cart = state.cart;
      if (!cart) return;

      setTxt('remudaHeaderCartCount', cart.item_count);

      const itemsContainer = document.getElementById('remudaCartItems');
      if (!itemsContainer) return;

      const errorHtml = state.cartError
        ? `<p class="cart-error" role="alert">${escapeHtml(state.cartError)}</p>`
        : '';

      if (!cart.items.length) {
        itemsContainer.innerHTML = errorHtml + '<p style="text-align:center;color:#717171;padding:32px 0;font-size:12px;">Your Shopping Bag is currently empty.</p>';
      } else {
        itemsContainer.innerHTML = errorHtml + cart.items.map((item) => {
          const key = escapeHtml(item.key);
          const showVariant = item.variant_title && item.variant_title !== 'Default Title';
          const saved = item.original_line_price > item.final_line_price;
          const discounts = (item.discounts || []).filter(d => d.amount > 0).map(d => `<div class="cart-item-meta cart-item-discount">${escapeHtml(d.title)}</div>`).join('');
          return `
            <div class="cart-item">
              <img src="${escapeHtml(sizedImage(item.image, 160))}" alt="${escapeHtml(item.product_title)}">
              <div>
                <div class="cart-item-title">${escapeHtml(item.product_title)}</div>
                ${showVariant ? `<div class="cart-item-meta">${escapeHtml(item.variant_title)}</div>` : ''}
                ${discounts}
                <div class="cart-qty">
                  <button type="button" onclick="RemudaStore.changeLine('${key}', ${item.quantity - 1})" aria-label="Remove one">−</button>
                  <span>${item.quantity}</span>
                  <button type="button" onclick="RemudaStore.changeLine('${key}', ${item.quantity + 1})" aria-label="Add one">+</button>
                  <button type="button" class="cart-remove" onclick="RemudaStore.changeLine('${key}', 0)">Remove</button>
                </div>
              </div>
              <div class="cart-item-price">
                ${saved ? `<s>${money(item.original_line_price)}</s>` : ''}
                <span>${money(item.final_line_price)}</span>
              </div>
            </div>
          `;
        }).join('');
      }

      setTxt('remudaCartSubtotal', money(cart.total_price));

      const savingRow = document.getElementById('remudaCartSavingRow');
      if (savingRow) savingRow.hidden = !cart.total_discount;
      setTxt('remudaCartSaving', '-' + money(cart.total_discount || 0));

      const checkoutBtn = document.getElementById('remudaCheckoutBtn');
      if (checkoutBtn) checkoutBtn.classList.toggle('is-disabled', !cart.items.length);

      document.querySelectorAll('[data-upsell-variant]').forEach((row) => {
        const inCart = cart.items.some(i => String(i.variant_id) === row.dataset.upsellVariant);
        row.hidden = inCart;
      });
    }
  };

  document.addEventListener('DOMContentLoaded', () => {
    window.RemudaStore.init();
  });
})();
